import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2 } from "@/components/icons";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { formatNlDate } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

// §3: a machine holds its OWN documents — manual, certificate, datasheet.
// Storage: application/pdf under the `asset-docs` prefix. The list stores
// {key, name, mime, size, uploaded_at, uploaded_by_name}; open opens via
// the internal /api/storage/file endpoint (auth-gated).
const humanSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const AssetDocuments = ({ asset, onChange }) => {
  const { canWrite } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const openDoc = async (d) => {
    try {
      const resp = await api.get(`/storage/file`, { params: { key: d.key }, responseType: "blob" });
      const blob = new Blob([resp.data], { type: d.mime || "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Revoke shortly after — Chrome/Safari need a tick to open it.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(getErrorMessage(e, "Openen mislukt."));
    }
  };

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Alleen PDF-bestanden — foto's leg je vast bij een storing of werkbon.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
      });
      toast.success(`Document toegevoegd: ${file.name}`);
      onChange && onChange();
    } catch (err) { toast.error(getErrorMessage(err, "Uploaden mislukt.")); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`"${d.name}" verwijderen van deze machine?`)) return;
    setBusy(true);
    try {
      await api.delete(`/assets/${asset.id}/documents`, { params: { key: d.key } });
      toast.success("Document verwijderd");
      onChange && onChange();
    } catch (err) { toast.error(getErrorMessage(err, "Verwijderen mislukt.")); }
    finally { setBusy(false); }
  };

  const docs = asset.documents || [];

  return (
    <Card className="p-5" data-testid="asset-documents">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-body font-semibold text-foreground">Documenten</h2>
          <p className="text-data text-muted-foreground">
            Handleiding, certificaat of datasheet die bij deze machine hoort. Foto's leg je vast bij
            een storing of werkbon — die vind je onder die tabbladen.
          </p>
        </div>
        {canWrite() && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              data-testid="asset-document-file-input"
              onChange={onFile}
            />
            <Button size="sm" data-testid="asset-document-upload-button" onClick={pick} disabled={busy}>
              <Upload size={18} className="mr-1.5" /> {busy ? "Bezig…" : "PDF toevoegen"}
            </Button>
          </>
        )}
      </div>
      {docs.length === 0 ? (
        <p className="py-4 text-body text-muted-foreground" data-testid="asset-documents-empty">
          Nog geen documenten aan deze machine gekoppeld.
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="asset-documents-list">
          {docs.map((d) => (
            <li key={d.key} data-testid={`asset-document-${d.key}`} className="flex items-center gap-3 py-3">
              <FileText size={20} className="shrink-0 text-inkt-zacht" />
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                data-testid={`asset-document-open-${d.key}`}
                onClick={() => openDoc(d)}
              >
                <div className="truncate text-body font-medium text-foreground hover:underline">{d.name}</div>
                <div className="text-data text-muted-foreground">
                  {[
                    humanSize(d.size),
                    d.uploaded_at ? `toegevoegd ${formatNlDate((d.uploaded_at || "").slice(0, 10))}` : null,
                    d.uploaded_by_name || null,
                  ].filter(Boolean).join(" · ")}
                </div>
              </button>
              {canWrite() && (
                <Button variant="ghost" size="icon" data-testid={`asset-document-delete-${d.key}`} onClick={() => remove(d)} title="Verwijderen">
                  <Trash2 size={18} />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
